import React, { ReactNode } from 'react'
import { cn } from 'lib/utils'
import { Input as ShadcnInput } from 'components/ui/input'
import { Label } from 'components/ui/label'

interface TWInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
	name?: string
	label?: string
	value?: any
	error?: string | null
	size?: string
	startIcon?: ReactNode
	endIcon?: ReactNode
	InputProps?: { startAdornment?: ReactNode; endAdornment?: ReactNode }
	InputLabelProps?: any
	inputRef?: React.Ref<HTMLInputElement>
	fullWidth?: boolean
	variant?: string
	helperText?: string
	[key: string]: any
}

const Input = React.forwardRef<HTMLInputElement, TWInputProps>(function Input(props, ref) {
	const {
		name,
		disabled,
		size,
		label,
		value,
		error = null,
		onChange,
		startIcon,
		endIcon,
		InputProps,
		InputLabelProps,
		inputRef,
		fullWidth,
		variant,
		helperText,
		className,
		...other
	} = props

	const combinedRef = inputRef || ref
	const resolvedStartIcon = startIcon || InputProps?.startAdornment
	const resolvedEndIcon = endIcon || InputProps?.endAdornment
	const hasIcons = resolvedStartIcon || resolvedEndIcon

	return (
		<div className={cn('tw-form-control w-full', className)}>
			{label && (
				<Label htmlFor={name} className="mb-1.5">
					{label}
				</Label>
			)}
			<div className={cn('relative', hasIcons && 'flex items-center')}>
				{resolvedStartIcon && (
					<span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">
						{resolvedStartIcon}
					</span>
				)}
				<ShadcnInput
					ref={combinedRef}
					name={name}
					type="text"
					disabled={disabled}
					value={value ?? ''}
					onChange={onChange}
					className={cn(
						size === 'small' && 'h-8 text-xs',
						resolvedStartIcon && 'pl-8',
						resolvedEndIcon && 'pr-8',
						error && 'border-destructive focus-visible:ring-destructive',
					)}
					{...other}
				/>
				{resolvedEndIcon && (
					<span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">
						{resolvedEndIcon}
					</span>
				)}
			</div>
			{error && <p className="mt-1 text-xs text-destructive">{error}</p>}
		</div>
	)
})

export default Input
